<template>
  <slot name="action" v-bind="$attrs" />

  <Teleport :to="target ?? '#overlay'">
    <transition name="appear">
      <div
        v-if="open"
        class="Modal"
        :style="{
          '--width': width ? `${width}px` : fullWidth ? '90vw' : null,
        }"
        v-bind="$attrs"
      >
        <div
          aria-hidden="true"
          class="backdrop"
          @click="
            () => {
              if (closeable) {
                emit('update:open', false);
              }
            }
          "
        />

        <div class="box">
          <slot name="header" />
          <div class="modal-content" :class="{ 'px-25': !noPadding, 'pb-16': !noPadding , 'full-height': fullHeight}">
            <slot />
          </div>
          <slot name="footer" />
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script lang="ts" setup>
import { watch } from 'vue';
import { onKeyStroke } from '@vueuse/core';
import Scroll from '@/utils/scrolllock';

interface Props {
  open: boolean;
  width?: number;
  closeable?: boolean;
  noPadding?: boolean;
  target?: string;
  fullHeight?: boolean;
  fullWidth?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  closeable: true,
});

const emit = defineEmits(['update:open']);

watch(
  () => props.open,
  (value) => {
    if (value) {
      Scroll.lock();
    } else {
      Scroll.unlock();
    }
  }
);

onKeyStroke('Escape', () => {
  if (props.closeable) {
    emit('update:open', false);
  }
});
</script>

<style lang="scss" scoped>
.Modal {
  position: fixed;
  z-index: 10000;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  backdrop-filter: blur(5px);
}
.backdrop {
  content: '';
  position: absolute;
  background: rgba(0, 0, 0, 0.5);
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
.box {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: var(--width, min-content);
  max-height: 90vh;
  max-width: 95%;
  overflow: hidden;
  background: white;
  backdrop-filter: blur(10px);
  display: flex;
  flex-flow: column;

  @apply rounded-4;
}
.modal-content {
  position: relative;
  min-height: 170px;
  max-height: min(90vh, 100%);
  max-width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
}
.full-height {
  height: 90vh;
}
</style>
